import { type HTMLAttributes, forwardRef } from 'react'
import { cn } from '@lib/utils'

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  /** Badge variant */
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info' | 'outline'
  /** Badge size */
  size?: 'sm' | 'md' | 'lg'
}

/**
 * Badge component for status indicators
 *
 * @example
 * <Badge>Default</Badge>
 * <Badge variant="success">Active</Badge>
 * <Badge variant="error">Error</Badge>
 */
export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant = 'default', size = 'md', ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(
          // Base styles
          'inline-flex items-center font-medium',

          // Size variants
          size === 'sm' && 'px-2 py-0.5 text-caption',
          size === 'md' && 'px-2.5 py-1 text-body-sm',
          size === 'lg' && 'px-3 py-1.5 text-body',

          // Variant styles
          variant === 'default' && 'bg-surface-elevated text-text-primary',
          variant === 'success' && 'bg-success-muted text-success',
          variant === 'warning' && 'bg-warning-muted text-warning',
          variant === 'error' && 'bg-error-muted text-error',
          variant === 'info' && 'bg-info-muted text-info',
          variant === 'outline' && 'bg-transparent border border-border text-text-secondary',

          className
        )}
        {...props}
      />
    )
  }
)

Badge.displayName = 'Badge'

/**
 * Rank badge for position display
 */
export interface RankBadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** Rank position (1-based) */
  position: number
  /** Size */
  size?: 'sm' | 'md' | 'lg'
}

export const RankBadge = forwardRef<HTMLSpanElement, RankBadgeProps>(
  ({ className, position, size = 'md', ...props }, ref) => {
    const getColors = () => {
      if (position === 1) return 'bg-rank-gold text-text-inverse'
      if (position <= 3) return 'bg-rank-silver text-text-inverse'
      if (position <= 10) return 'bg-rank-bronze text-text-inverse'
      return 'bg-surface-overlay text-text-primary'
    }

    return (
      <span
        ref={ref}
        className={cn(
          // Base styles
          'inline-flex items-center justify-center font-bold tabular-nums',

          // Size variants
          size === 'sm' && 'min-w-6 h-6 px-1.5 text-caption',
          size === 'md' && 'min-w-8 h-8 px-2 text-body-sm',
          size === 'lg' && 'min-w-10 h-10 px-2.5 text-body',

          // Position colors
          getColors(),

          className
        )}
        {...props}
      >
        #{position}
      </span>
    )
  }
)

RankBadge.displayName = 'RankBadge'

/**
 * Status dot indicator
 */
export interface StatusDotProps extends HTMLAttributes<HTMLSpanElement> {
  /** Status type */
  status: 'online' | 'offline' | 'busy' | 'away'
  /** Size */
  size?: 'sm' | 'md' | 'lg'
  /** Show pulse animation */
  pulse?: boolean
}

export const StatusDot = forwardRef<HTMLSpanElement, StatusDotProps>(
  ({ className, status, size = 'md', pulse = false, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(
          // Base styles
          'inline-block rounded-full',

          // Size
          size === 'sm' && 'h-2 w-2',
          size === 'md' && 'h-2.5 w-2.5',
          size === 'lg' && 'h-3 w-3',

          // Status colors
          status === 'online' && 'bg-success',
          status === 'offline' && 'bg-text-muted',
          status === 'busy' && 'bg-error',
          status === 'away' && 'bg-warning',

          // Pulse animation
          pulse && status === 'online' && 'animate-pulse',

          className
        )}
        {...props}
      />
    )
  }
)

StatusDot.displayName = 'StatusDot'

export default Badge
